import { call, put, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import { tracker } from '../../utils/core';
import { resourceSagaUtils, resourceStoreTypes } from '../core';

import * as actions from './memorizedRulesActions';
import { getLastSyncDate } from './memorizedRulesSelectors';
import { transformMemorizedRuleToRequestData, transformResponseToMemorizedRules } from './memorizedRulesTransformers';
import { mkMemorizedRule } from './memorizedRulesTypes';

// ===============================================================================================
// SAGAS functions for memorizedRules.  These are the functions responsible for communicating directly
// with external endpoints asynchronously.

// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

const mkResourceConfig = (successAction, failureAction) =>
  resourceStoreTypes.mkQcsSyncResourceConfig({
    resourceName: 'memorizedRule',
    resourceBaseUrl: '/memorized-rules',
    getLastSyncDate,

    transformResponseToResources: transformResponseToMemorizedRules,
    transformResourceToRequestData: transformMemorizedRuleToRequestData,

    successAction,
    failureAction,
  });

export function* makeMemorizedRuleFromTransaction(action) {
  const txn = action.payload;
  const newRule = mkMemorizedRule({
    clientId: uuidv4().toUpperCase(),
    payee: txn.payee,
    coa: txn.coa,
  });
  yield put(actions.createMemorizedRule(newRule, { props: { transaction: txn } }));
}

export function* getMemorizedRulesActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.getMemorizedRulesSuccess, actions.getMemorizedRulesFailure);
  yield resourceSagaUtils.takeAndDebounce(
    actions.getMemorizedRules,
    resourceSagaUtils.qcsSyncGetResources,
    resourceConfig,
  );
}

export function* makeMemorizedRuleFromTransactionActionWatcher() {
  yield takeEvery(actions.makeMemorizedRuleFromTransactionAction, makeMemorizedRuleFromTransaction);
}

export function* createMemorizedRuleActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.createMemorizedRuleSuccess, actions.createMemorizedRuleFailure);
  yield takeEvery(actions.createMemorizedRule, function* createMemorizedRule(action) {
    yield call(resourceSagaUtils.qcsSyncCreateResource, resourceConfig, action);
    tracker.track(tracker.events.categoryRuleCreate, {
      initiator: action.meta?.props?.transaction ? 'transaction' : 'settings',
      apply_past: action.meta?.props?.applyToTransactions,
    });
  });
}

export function* updateMemorizedRuleActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.updateMemorizedRuleSuccess, actions.updateMemorizedRuleFailure);
  yield takeEvery(actions.updateMemorizedRule, function* updateMemorizedRule(action) {
    yield call(resourceSagaUtils.qcsSyncUpdateResource, resourceConfig, action);
    tracker.track(tracker.events.categoryRuleEdit, {
      apply_past: action.meta?.props?.applyToTransactions,
    });
  });
}

export function* deleteMemorizedRuleActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.deleteMemorizedRuleSuccess, actions.deleteMemorizedRuleFailure);
  yield takeEvery(actions.deleteMemorizedRule, function* deleteMemorizedRule(action) {
    yield call(resourceSagaUtils.qcsSyncDeleteResource, resourceConfig, action);
    tracker.track(tracker.events.categoryRuleDelete);
  });
}

export function* combineMemorizedRulesActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.combineMemorizedRulesSuccess, actions.combineMemorizedRulesFailure);
  yield takeEvery(actions.combineMemorizedRules, function* combineMemorizedRules(action) {
    yield call(resourceSagaUtils.qcsSyncUpdateResources, resourceConfig, action);
  });
}

// ====================================================
// EXPORTS

export default [
  getMemorizedRulesActionWatcher,
  createMemorizedRuleActionWatcher,
  updateMemorizedRuleActionWatcher,
  deleteMemorizedRuleActionWatcher,
  makeMemorizedRuleFromTransactionActionWatcher,
  combineMemorizedRulesActionWatcher,
];
